import { type Dispatch, type SetStateAction } from 'react';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import { ImageAndTextOverlay } from 'Components/ImageAndTextOverlay';
import Spacer from 'Components/Spacer';
import { Typography } from 'Components/Typography';

import { clinicallyTestedContent } from 'assets/content/homeV2';

import useResponsiveVariant from 'utils/useResponsiveVariant';

import { DesktopInfoTooltip, MobileInfoIconTooltip } from './InfoIconTooltip';

const TextContainer = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  text-align: center;
  padding: 0 ${theme.spacing['10x']};
`;

const StyledEybrow = styled(Typography)`
  font-family: ${theme.typography.fontFamily.label};
  text-transform: uppercase;
`;

const StyledTitle = styled(Typography)`
  position: relative;

  font-size: 24px;
  font-family: ${theme.typography.fontFamily.body};
  ${legacyTheme.breakpoints.up('md')} {
    margin-right: ${theme.spacing['4x']};
    font-size: ${theme.typography.fontSize['3xl']};
  }
`;

const StyledSpan = styled.span`
  ${legacyTheme.breakpoints.up('md')} {
    margin-right: ${theme.spacing['4x']};
  }
`;

type ClinicallyTestedProps = {
  setIsOpen: Dispatch<SetStateAction<boolean>>;
};

export const ClinicallyTested = ({ setIsOpen }: ClinicallyTestedProps) => {
  const { isMobile, isDesktop, isTablet } = useResponsiveVariant();
  const { title, eyebrow, imgAlt, imgMobile, imgDesktop, tooltipContent } = clinicallyTestedContent;

  return (
    <ImageAndTextOverlay
      dataTestId="home-revamp-clinically-tested"
      imgData={{ imgDesktop, imgMobile, imgAlt }}
    >
      <TextContainer>
        <StyledEybrow color="white" markupName="p">
          {eyebrow}
        </StyledEybrow>
        <Spacer axis="vertical" size={8} />

        <StyledTitle color="white" markupName="h2">
          <StyledSpan>{title}</StyledSpan>
          {(isMobile || isTablet) && <MobileInfoIconTooltip setIsOpen={setIsOpen} />}
          {isDesktop && <DesktopInfoTooltip tooltipContent={tooltipContent} />}
        </StyledTitle>
      </TextContainer>
    </ImageAndTextOverlay>
  );
};
